import { useNavigate } from 'react-router-dom';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import {
  FileText, Calendar, Trees, Ruler, Weight, Package,
  CheckCircle, Clock, XCircle, MessageSquare,
  ChevronRight
} from 'lucide-react';
import type { ProductionDeclaration } from '../../types/cultivar';
import PbrMark from './PbrMark';
import { formatNumeric } from '@/utils/numeric';

interface DeclarationCardProps {
  declaration: ProductionDeclaration;
  userRole: 'declarant' | 'licensor';
  onAcknowledge?: () => void;
  onQuery?: (message: string) => void;
  onReject?: (reason: string) => void;
}

const STATUS_CONFIG: Record<string, {
  label: string;
  icon: typeof CheckCircle;
  bgColor: string;
  textColor: string;
}> = {
  declared: {
    label: 'Declared',
    icon: Clock,
    bgColor: 'bg-primary-100',
    textColor: 'text-primary-700',
  },
  acknowledged: {
    label: 'Acknowledged',
    icon: CheckCircle,
    bgColor: 'bg-success-100',
    textColor: 'text-success-700',
  },
  queried: {
    label: 'Query Raised',
    icon: MessageSquare,
    bgColor: 'bg-warning-100',
    textColor: 'text-warning-700',
  },
  revised: {
    label: 'Revised',
    icon: FileText,
    bgColor: 'bg-primary-100',
    textColor: 'text-primary-700',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    bgColor: 'bg-error-100',
    textColor: 'text-error-700',
  },
};

function DeclarationCard({
  declaration,
  userRole,
  onAcknowledge,
}: DeclarationCardProps) {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const statusConfig = STATUS_CONFIG[declaration.status] || STATUS_CONFIG.declared;
  const StatusIcon = statusConfig.icon;

  const canReview = userRole === 'licensor' &&
    ['declared', 'revised'].includes(declaration.status);

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        {/* Query Message Banner */}
        {declaration.status === 'queried' && declaration.query_message && (
          <div className="mb-4 -mt-2 -mx-2 px-4 py-3 bg-warning-50 border-b border-warning-200 rounded-t-lg">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-warning-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-warning-800">Query from License Holder</p>
                <p className="text-sm text-warning-700 mt-1">{declaration.query_message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-neutral-900 truncate">
                {declaration.cultivar_name}
                {/* Production declarations exist only under PBR licences — force the mark. */}
                <PbrMark status="protected" />
              </h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Calendar className="h-4 w-4" />
              <span>Season {declaration.season_year}</span>
              <span>•</span>
              <span>{declaration.declaring_org_name}</span>
            </div>
          </div>
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium
            ${statusConfig.bgColor} ${statusConfig.textColor}
          `}>
            <StatusIcon className="h-4 w-4" />
            {statusConfig.label}
          </div>
        </div>

        {/* Production Data Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-neutral-50 rounded-lg">
          <div className="text-center">
            <Trees className="h-5 w-5 text-success-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-neutral-900">
              {formatNumeric(declaration.trees_planted)}
            </p>
            <p className="text-xs text-neutral-500">Trees Planted</p>
          </div>
          <div className="text-center">
            <Ruler className="h-5 w-5 text-primary-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-neutral-900">
              {formatNumeric(declaration.hectares_planted)}
            </p>
            <p className="text-xs text-neutral-500">Hectares</p>
          </div>
          <div className="text-center">
            <Weight className="h-5 w-5 text-warning-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-neutral-900">
              {formatNumeric(declaration.actual_production_tonnes)}
            </p>
            <p className="text-xs text-neutral-500">Actual (t)</p>
          </div>
          <div className="text-center">
            <Package className="h-5 w-5 text-purple-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-neutral-900">
              {formatNumeric(declaration.packed_tonnes)}
            </p>
            <p className="text-xs text-neutral-500">Packed (t)</p>
          </div>
        </div>

        {/* Additional Info */}
        {(declaration.rootstock || declaration.rootstock_cultivar_name || declaration.planting_date) && (
          <div className="flex flex-wrap gap-3 mb-4 text-sm text-neutral-600">
            {(declaration.rootstock_cultivar_name || declaration.rootstock) && (
              <span className="px-2 py-1 bg-neutral-100 rounded">
                Rootstock: {declaration.rootstock_cultivar_name || declaration.rootstock}
              </span>
            )}
            {declaration.planting_date && (
              <span className="px-2 py-1 bg-neutral-100 rounded">
                Planted: {formatLocalDateInTz(declaration.planting_date, timezone)}
              </span>
            )}
          </div>
        )}

        {/* Acknowledgment Info */}
        {declaration.status === 'acknowledged' && declaration.acknowledged_at && (
          <div className="mb-4 p-3 bg-success-50 border border-success-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-success-800">
              <CheckCircle className="h-4 w-4" />
              <span>
                Acknowledged by {declaration.acknowledged_by_org_name} on{' '}
                {formatDateInTz(declaration.acknowledged_at, timezone)}
              </span>
            </div>
            {declaration.acknowledgment_notes && (
              <p className="text-sm text-success-700 mt-1">
                Notes: {declaration.acknowledgment_notes}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t border-neutral-100">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/cultivar/declarations/${declaration.id}`)}
            className="flex-1"
          >
            View Details
            <ChevronRight className="h-4 w-4" />
          </Button>

          {canReview && onAcknowledge && (
            <Button variant="success" size="sm" onClick={onAcknowledge}>
              <CheckCircle className="h-4 w-4" />
              Acknowledge
            </Button>
          )}

          {declaration.status === 'acknowledged' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/cultivar/declarations/${declaration.id}/certificate`)}
            >
              Certificate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default DeclarationCard;
